import React, { useEffect, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    TextField,
    Button,
    RadioGroup,
    FormControlLabel,
    Radio,
    FormControl,
    FormLabel,
    Paper,
    useTheme,
    CircularProgress,
} from "@mui/material";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";

import figLogo from "../../assets/northbergenlogo.png";
import StripeForm from "../StripeForm/StripeForm";
import axios from "axios";

/* ============================================================
   PaymentCollectionForm
   ============================================================ */
const PaymentCollectionForm = ({
    open,
    onClose,
    studentInfo = {}, // { apiid, balance }
}) => {
    const theme = useTheme();

    /* guardian (backend/export only) */
    const [parentName, setParentName] = useState("");
    const [email, setEmail] = useState("");

    /* payer-visible inputs */
    const [payerName, setPayerName] = useState("");
    const [payerEmail, setPayerEmail] = useState("");

    const [paymentMethod, setPaymentMethod] = useState("CC");
    const [amount, setAmount] = useState("");
    const [studentDetails, setStudentDetails] = useState(null);
    const [loading, setLoading] = useState(true);

    /* helpers */
    const currencyFmt = (n) =>
        (n ?? 0).toLocaleString("en-US", {
            style: "currency",
            currency: "USD",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    const toNumber = (val) => {
        const num = parseFloat(String(val ?? "").replace(/[^0-9.-]/g, ""));
        return Number.isFinite(num) ? num : 0;
    };

    /* fetch student + guardian */
    useEffect(() => {
        let isMounted = true;
        (async () => {
            if (!studentInfo.apiid) {
                setLoading(false);
                return;
            }
            try {
                const { data } = await axios.get(
                    `https://figazuresql.swancreate.com/api/swa/students/${studentInfo.apiid}`
                );
                if (!isMounted) return;
                setStudentDetails(data);
                setParentName(
                    `${data.guardian_FName ?? ""} ${data.guardian_LName ?? ""}`.trim()
                );
                setEmail(data.guardian_Email || "");
            } catch (err) {
                console.error("Error fetching student details:", err);
            } finally {
                if (isMounted) setLoading(false);
            }
        })();
        return () => {
            isMounted = false;
        };
    }, [studentInfo.apiid]);

    /* reset inputs on each open */
    useEffect(() => {
        if (open) {
            setPayerName("");
            setPayerEmail("");
            setAmount("");
        }
    }, [open]);

    /* balance calc */
    const rawBalance =
        studentInfo.balance ??
        studentInfo.Balance ??
        studentInfo.balanceDue ??
        studentInfo.Account_Balance ??
        studentDetails?.accountBalance ??
        studentDetails?.AccountBalance ??
        studentDetails?.Account_Balance ??
        0;
    const balanceNumber = toNumber(rawBalance);
    const balanceDisplay = currencyFmt(Math.abs(balanceNumber));
    const balanceColor = balanceNumber < 0 ? "#D32F2F" : theme.palette.text.primary;

    /* amount handlers */
    const MAX_AMOUNT = 9999.99;
    const handleAmountChange = (e) => {
        const raw = e.target.value;
        if (raw === "") return setAmount("");
        if (!/^\$?\d{0,4}(\.\d{0,2})?$/.test(raw)) return;
        setAmount(raw);
    };
    const handleAmountBlur = () => {
        if (amount === "") return;
        let num = toNumber(amount);
        if (num > MAX_AMOUNT) num = MAX_AMOUNT;
        setAmount(currencyFmt(num));
    };

    /* shared TextField styles */
    const commonFieldSX = {
        backgroundColor: "#FFFFFF",
        "& .MuiOutlinedInput-notchedOutline": { borderColor: "#90A4AE" },
        "& .MuiOutlinedInput-root": { color: "#263238" },
        "& input": { fontWeight: 600, fontSize: "1.25rem", color: "#0D47A1" },
        "& .MuiInputLabel-root": { color: "#003366", fontWeight: 600 },
        "& .MuiInputLabel-root.Mui-focused": { color: "#003366" },
    };

    /* Stripe form */
    const renderMethodFields = () => {
        const numericAmount = toNumber(amount);

        // choose payer-entered first, fallback to guardian data
        const stripeName = payerName.trim() || parentName.trim();
        const stripeEmail = payerEmail.trim() || email.trim();

        return (
            <StripeForm
                amount={numericAmount}
                parentName={stripeName}
                email={stripeEmail}
                studentDetails={studentDetails}
                paymentMethod={paymentMethod === "CC" ? "card" : "us_bank_account"}
            />
        );
    };

    /* UI */
    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{
                elevation: 4,
                sx: {
                    borderRadius: 3,
                    bgcolor: "#FAFAFA",
                    color: "#263238",
                    p: 2,
                },
            }}
        >
            {/* Header */}
            <DialogTitle sx={{ textAlign: "center", pb: 0 }}>
                <img
                    src={figLogo}
                    alt="FIG Logo"
                    style={{ width: 120, marginBottom: theme.spacing(1) }}
                />
                <Typography variant="h6">Make a Payment</Typography>
            </DialogTitle>

            {/* Content */}
            <DialogContent dividers sx={{ mt: 1 }}>
                {loading ? (
                    <Box textAlign="center" py={4}>
                        <CircularProgress />
                    </Box>
                ) : studentDetails ? (
                    <>
                        {/* Student summary */}
                        <Paper
                            variant="outlined"
                            sx={{
                                bgcolor: "#FFFFFF",
                                p: 2,
                                mb: 3,
                                borderRadius: 2,
                                borderColor: "#90A4AE",
                            }}
                        >
                            <Typography variant="h6" sx={{ color: "#003366", fontSize: "1.1rem" }}>
                                Student:&nbsp;
                                <Box component="span" sx={{ color: "#cc0000", fontWeight: 700 }}>
                                    {studentDetails.FirstName} {studentDetails.LastName}
                                </Box>
                            </Typography>
                            <Typography variant="h6" sx={{ color: "#003366", fontSize: "1.1rem" }}>
                                School:&nbsp;
                                <Box component="span" sx={{ color: "#cc0000", fontWeight: 700 }}>
                                    {studentDetails.SchoolName}
                                </Box>
                            </Typography>
                            <Typography
                                variant="h6"
                                fontWeight="bold"
                                mt={1}
                                sx={{ color: balanceColor, fontSize: "1.1rem" }}
                            >
                                Balance&nbsp;Due:&nbsp;{balanceDisplay}
                            </Typography>
                        </Paper>

                        {/* Payer inputs */}
                        <TextField
                            label="Payer Name"
                            fullWidth
                            margin="normal"
                            value={payerName}
                            onChange={(e) => setPayerName(e.target.value)}
                            placeholder="Name of person paying"
                            sx={commonFieldSX}
                        />
                        <TextField
                            label="Email"
                            type="email"
                            fullWidth
                            margin="normal"
                            value={payerEmail}
                            onChange={(e) => setPayerEmail(e.target.value)}
                            placeholder="Receipt will be sent here"
                            sx={commonFieldSX}
                        />

                        {/* Amount */}
                        <TextField
                            label="Amount to Pay"
                            fullWidth
                            margin="normal"
                            value={amount}
                            onChange={handleAmountChange}
                            onBlur={handleAmountBlur}
                            placeholder="$0.00"
                            sx={commonFieldSX}
                            inputProps={{
                                inputMode: "decimal",
                                pattern: "^\\$?\\d{0,4}(\\.\\d{0,2})?$",
                            }}
                        />

                        {/* Payment method */}
                            <FormControl component="fieldset" sx={{ mt: 3 }}>
                                <FormLabel component="legend" sx={{ color: "#263238" }}>
                                    Payment Method
                                </FormLabel>
                                <RadioGroup
                                    row
                                    value={paymentMethod}
                                    onChange={(e) => setPaymentMethod(e.target.value)}
                                >
                                    <FormControlLabel
                                        value="CC"
                                        control={
                                            <Radio
                                                icon={<CreditCardIcon sx={{ color: theme.palette.primary.main }} />}
                                                checkedIcon={<CreditCardIcon sx={{ color: theme.palette.primary.main }} />}
                                            />
                                        }
                                        label={
                                            <Typography sx={{ color: theme.palette.primary.main }}>
                                                Credit&nbsp;Card
                                            </Typography>
                                        }
                                    />
                                </RadioGroup>
                            </FormControl>


                        {/* Stripe fields */}
                        {renderMethodFields()}
                    </>
                ) : (
                    <Typography color="error">Student data not found.</Typography>
                )}
            </DialogContent>

            {/* Cancel */}
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} sx={{ color: "#1976D2" }}>
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PaymentCollectionForm;
